import React, { useState, useEffect } from 'react';
import { Shield, Loader2, User, Mail, Calendar, Plus, Trash2, X, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import api from './utils/api';
import { useAuth } from './context/AuthContext';

interface AdminUser {
  id: string;
  name: string;
  email: string;
  createdAt: any;
}

const AdminApprovals: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Add Admin Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });

  const fetchAdmins = async () => {
    try {
      const response = await api.get('/admin/list');
      setAdmins(response.data);
    } catch (error) {
      console.error('Error fetching admins:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAddAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      await api.post('/admin/create', formData);
      setSuccess('Administrator created successfully!');
      setFormData({ name: '', email: '', password: '' });
      // Refetch list
      await fetchAdmins();
      // Close modal after a short delay
      setTimeout(() => {
        setShowAddModal(false);
        setSuccess('');
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create administrator');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAdmin = async (id: string) => {
    if (!window.confirm('Are you sure you want to revoke access for this administrator?')) {
      return;
    }

    setDeletingId(id);
    try {
      await api.delete(`/admin/${id}`);
      setAdmins((prev) => prev.filter((admin) => admin.id !== id));
    } catch (error) {
      console.error('Error deleting admin:', error);
      alert('Failed to delete admin.');
    } finally {
      setDeletingId(null);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="w-10 h-10 text-[#05A664] animate-spin mb-4" />
        <p className="text-[#909090] font-medium">Loading administrators...</p>
      </div>
    );
  }

  return (
    <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-[#F1F8F5] rounded-xl flex items-center justify-center">
              <Shield className="text-[#05A664] w-5 h-5" />
            </div>
            <h1 className="text-3xl font-extrabold text-[#121415] tracking-tight">Admin Management</h1>
          </div>
          <p className="text-[#909090] font-medium">View current administrators and manage access credentials.</p>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center justify-center gap-2 bg-[#121415] text-white font-bold px-6 py-3.5 rounded-2xl hover:bg-[#05A664] active:scale-[0.98] transition-all shadow-lg shadow-black/10 self-start md:self-center"
        >
          <Plus size={18} />
          Add New Admin
        </button>
      </div>

      {/* Admins Grid Container */}
      <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-8 border-b border-gray-50 flex items-center justify-between">
          <h2 className="text-xl font-bold text-[#121415]">Active Administrators</h2>
          <span className="px-4 py-1.5 bg-[#F1F8F5] text-[#05A664] text-xs font-black rounded-full uppercase tracking-wider">
            {admins.length} Active
          </span>
        </div>

        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {admins.map((admin) => (
                <motion.div
                  key={admin.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="bg-white border border-gray-100 hover:border-[#05A664]/20 rounded-3xl p-6 shadow-sm flex flex-col justify-between gap-6 group relative overflow-hidden transition-all"
                >
                  {currentUser?.id === admin.id && (
                    <span className="absolute top-4 right-4 bg-blue-50 text-blue-600 text-[10px] font-black px-2.5 py-1 rounded-full border border-blue-100 uppercase tracking-wider">
                      You
                    </span>
                  )}

                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="h-14 w-14 rounded-2xl bg-gradient-to-tr from-[#F1F8F5] to-[#DEF2EA] flex items-center justify-center font-black text-[#05A664] text-lg border border-[#05A664]/10 group-hover:scale-105 transition-transform duration-300">
                        {getInitials(admin.name)}
                      </div>
                      <div>
                        <h3 className="text-lg font-extrabold text-[#121415] tracking-tight group-hover:text-[#05A664] transition-colors truncate max-w-[180px]">
                          {admin.name}
                        </h3>
                        <div className="flex items-center gap-1 text-[#909090] text-sm font-medium">
                          <Mail size={12} className="flex-shrink-0" />
                          <span className="truncate max-w-[180px]">{admin.email}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-[#909090] bg-gray-50/50 p-3.5 rounded-2xl">
                      <Calendar size={14} className="text-gray-400" />
                      <span className="text-xs font-bold">
                        Joined: {admin.createdAt ? new Date(admin.createdAt._seconds * 1000).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                  </div>

                  {currentUser?.id !== admin.id && (
                    <div className="pt-4 border-t border-gray-50 flex">
                      <button
                        onClick={() => handleDeleteAdmin(admin.id)}
                        disabled={deletingId === admin.id}
                        className="flex items-center gap-1.5 text-red-500 hover:text-red-700 font-bold text-xs transition-colors py-1.5 px-3 hover:bg-red-50 rounded-xl ml-auto border border-transparent hover:border-red-100"
                      >
                        {deletingId === admin.id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Trash2 size={14} />
                        )}
                        Revoke Access
                      </button>
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Add Admin Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#121415]/40 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ type: "spring", bounce: 0.2 }}
              className="bg-white w-full max-w-md rounded-[2.5rem] shadow-2xl overflow-hidden border border-white/20 flex flex-col"
            >
              {/* Header */}
              <div className="p-6 md:p-8 border-b border-gray-50 flex items-center justify-between bg-gray-50/50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#05A664]/10 rounded-xl flex items-center justify-center">
                    <Shield className="text-[#05A664] w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-black text-lg text-[#121415]">Add Administrator</h3>
                    <p className="text-[10px] font-black text-[#05A664] uppercase tracking-[0.1em]">Direct Creation</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="w-10 h-10 flex items-center justify-center bg-white border border-gray-100 rounded-xl hover:text-red-500 hover:border-red-100 hover:shadow-sm transition-all text-gray-400"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Form Content */}
              <form onSubmit={handleAddAdmin} className="p-6 md:p-8 space-y-5">
                {error && (
                  <div className="p-4 bg-red-50 border border-red-100 rounded-2xl text-red-600 text-sm font-bold flex items-center gap-2">
                    <AlertCircle size={16} className="flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
                {success && (
                  <div className="p-4 bg-green-50 border border-green-100 rounded-2xl text-green-600 text-sm font-bold flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-green-600 rounded-full animate-ping" />
                    <span>{success}</span>
                  </div>
                )}

                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-[#909090] uppercase tracking-[0.1em] ml-1">Full Name</label>
                  <div className="relative">
                    <User className="absolute inset-y-0 left-0 pl-5 h-full flex items-center pointer-events-none text-gray-300" size={18} />
                    <input
                      required
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g. John Doe"
                      className="w-full bg-gray-50 border border-gray-100 rounded-2xl py-4 pl-12 pr-4 text-[#121415] font-bold placeholder-[#B0B0B0] focus:outline-none focus:border-[#05A664] focus:bg-white transition-all"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-[#909090] uppercase tracking-[0.1em] ml-1">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute inset-y-0 left-0 pl-5 h-full flex items-center pointer-events-none text-gray-300" size={18} />
                    <input
                      required
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="admin@travelpass.com"
                      className="w-full bg-gray-50 border border-gray-100 rounded-2xl py-4 pl-12 pr-4 text-[#121415] font-bold placeholder-[#B0B0B0] focus:outline-none focus:border-[#05A664] focus:bg-white transition-all"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-[#909090] uppercase tracking-[0.1em] ml-1">Initial Password</label>
                  <div className="relative">
                    <div className="absolute left-5 top-0 bottom-0 flex items-center pointer-events-none text-gray-300">
                      <span className="font-bold text-xs">***</span>
                    </div>
                    <input
                      required
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="••••••••"
                      minLength={6}
                      className="w-full bg-gray-50 border border-gray-100 rounded-2xl py-4 pl-12 pr-4 text-[#121415] font-bold placeholder-[#B0B0B0] focus:outline-none focus:border-[#05A664] focus:bg-white transition-all"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-4 mt-2 border-t border-gray-50">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 py-4 font-bold border border-gray-100 rounded-2xl hover:bg-gray-50 active:scale-95 transition-all text-[#909090]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-[#121415] text-white font-black py-4 rounded-2xl hover:bg-[#05A664] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-xl shadow-black/5 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      'Create Admin'
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminApprovals;
